import { LoadingGate } from "@/components/LoadingGate/LoadingGate";
import { useAuthDetails } from "@/context/auth-context";
import { useGroupUserMeData } from "@/data/use-group-user-me-data";
import { useGroupData } from "@/data/use-groups-data";
import { useGroupUuid } from "@/hooks/use-group-uuid.tsx";
import { Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { canOpenMembershipSettings } from "./membership-settings-access";

/**
 * Users management. Super, this workspace's admin, an organization admin,
 * or a team-group admin on their personal workspace.
 */
export default function MembershipRoleGuard() {
  const authDetails = useAuthDetails();
  const groupUuid = useGroupUuid();
  const { group, isLoading: isGroupLoading } = useGroupData(groupUuid);
  const { groupRole, organizationRole, teamGroupAdmin, isLoading } =
    useGroupUserMeData(groupUuid);
  const navigate = useNavigate();
  const redirected = useRef(false);

  const isSuper = authDetails?.role === "super";
  // Wait for workspace details before deciding personal-workspace access.
  const waitingForGroup = isGroupLoading && !group;
  const waiting = authDetails === undefined || isLoading || waitingForGroup;
  const allowed =
    !waiting &&
    canOpenMembershipSettings({
      isSuper,
      groupRole,
      organizationRole,
      teamGroupAdmin,
      isPersonalGroup: group?.personal === true,
    });

  useEffect(() => {
    if (waiting) return;
    if (!allowed && !redirected.current) {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        navigate({ to: "/u/workspaces", replace: true });
      }
      redirected.current = true;
    }
  }, [allowed, navigate, waiting]);

  return (
    <LoadingGate loading={waiting}>{allowed ? <Outlet /> : null}</LoadingGate>
  );
}
